import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Plus, Copy, Check, MapPin, X, RefreshCw, Users, Activity, Clock, Trash2, Pencil, ArrowRightLeft, Sliders, History, Grid3X3, UserCircle, PauseCircle, Layers, Settings2, Building2, Video, Monitor, Wifi, WifiOff, Eye, Maximize2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import ZoneEditor from '../components/zones/ZoneEditor';
import CameraSnapshotThumbnail from '../components/CameraSnapshotThumbnail';
import toast from 'react-hot-toast';
import { camerasApi, branchesApi, organizationsApi, zonesApi, snapshotsApi } from '../services/api';
import type { SnapshotThumbnailHandle } from '../components/CameraSnapshotThumbnail';
import { useAuthStore } from '../services/auth';
import { useDashboardSocket } from '../hooks/useDashboardSocket';
import { useTimezone, formatInTimezone, formatTimeInTimezone } from '../hooks/useTimezone';
import type { Camera as CameraType, CameraWithKey, DetectionMode, Zone, LatestZoneCount } from '../types';

function CameraCard({ camera, getZonesForCamera, zoneCounts, onClick, refreshKey, isLiveFeed, isRequesting, onToggleLiveFeed, onRequestSnapshot, timezone }: {
  camera: CameraType;
  getZonesForCamera: (id: string) => Zone[];
  zoneCounts: Map<string, Map<string, number>>;
  onClick: () => void;
  refreshKey?: number;
  isLiveFeed?: boolean;
  isRequesting?: boolean;
  onToggleLiveFeed?: () => void;
  onRequestSnapshot?: () => void;
  timezone: string;
}) {
  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
      onClick={onClick}
    >
      <div className="relative group">
        <CameraSnapshotThumbnail
          cameraId={camera.id}
          cameraName={camera.name}
          isOnline={camera.is_online}
          zones={getZonesForCamera(camera.id)}
          zoneCounts={zoneCounts.get(camera.id) || new Map()}
          refreshInterval={isLiveFeed ? 3000 : 300000}
          refreshKey={refreshKey}
        />
        {/* Overlay controls - visible on hover or when live feed is active */}
        {camera.is_online && (
          <div className={`absolute bottom-2 right-2 flex gap-1.5 transition-opacity ${isLiveFeed ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <button
              onClick={(e) => { e.stopPropagation(); onRequestSnapshot?.(); }}
              disabled={isRequesting}
              className="p-1.5 rounded-md bg-black/60 hover:bg-black/80 text-white backdrop-blur-sm transition-colors"
              title="New Snapshot"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRequesting ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleLiveFeed?.(); }}
              className={`p-1.5 rounded-md backdrop-blur-sm transition-colors ${
                isLiveFeed
                  ? 'bg-green-600/80 hover:bg-green-600 text-white'
                  : 'bg-black/60 hover:bg-black/80 text-white'
              }`}
              title={isLiveFeed ? 'Stop Live Feed' : 'Start Live Feed'}
            >
              <Video className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {/* Live feed indicator */}
        {isLiveFeed && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600/80 text-white text-xs font-medium backdrop-blur-sm">
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
        )}
      </div>
      <div className="flex items-start justify-between">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${!camera.is_active ? 'bg-red-50 dark:bg-red-900/30' : camera.is_online ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-slate-700'}`}>
            {camera.camera_type === 'rtsp' ? (
              <Video className={`w-5 h-5 ${!camera.is_active ? 'text-red-500' : camera.is_online ? 'text-green-600' : 'text-gray-400 dark:text-slate-500'}`} />
            ) : camera.camera_type === 'dvr' ? (
              <Monitor className={`w-5 h-5 ${!camera.is_active ? 'text-red-500' : camera.is_online ? 'text-green-600' : 'text-gray-400 dark:text-slate-500'}`} />
            ) : (
              <Camera className={`w-5 h-5 ${!camera.is_active ? 'text-red-500' : camera.is_online ? 'text-green-600' : 'text-gray-400 dark:text-slate-500'}`} />
            )}
          </div>
          <div className="ml-3">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-gray-900 dark:text-white">{camera.name}</h3>
              {camera.camera_type === 'dvr' && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  DVR{camera.dvr_channel ? ` Ch${camera.dvr_channel}` : ''}
                </span>
              )}
              {camera.camera_type === 'rtsp' && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  RTSP
                </span>
              )}
              {camera.client_node_id && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300" title={`Client: ${camera.client_node_id}`}>
                  Node {camera.client_node_id.slice(0, 6)}
                </span>
              )}
            </div>
            {camera.location && (
              <div className="flex items-center text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                <MapPin className="w-3 h-3 mr-1" />
                {camera.location}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t dark:border-slate-700">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-slate-400">Last seen</span>
          <span className="text-gray-900 dark:text-white">
            {formatInTimezone(camera.last_seen_at, timezone)}
          </span>
        </div>
      </div>
    </Card>
  );
}

export default function Cameras() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<CameraType | null>(null);
  const [newCamera, setNewCamera] = useState<CameraWithKey | null>(null);
  const [regeneratedKey, setRegeneratedKey] = useState<CameraWithKey | null>(null);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editBranchId, setEditBranchId] = useState<string>('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showZoneEditor, setShowZoneEditor] = useState(false);
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [addBranchId, setAddBranchId] = useState<string>('');
  const [liveFeedActive, setLiveFeedActive] = useState(false);
  const [requestingSnapshot, setRequestingSnapshot] = useState(false);
  const [liveFeedCameraId, setLiveFeedCameraId] = useState<string | null>(null);
  const [requestingSnapshotId, setRequestingSnapshotId] = useState<string | null>(null);
  const [snapshotRefreshKeys, setSnapshotRefreshKeys] = useState<Record<string, number>>({});
  const [addCameraTab, setAddCameraTab] = useState<'client' | 'dvr' | 'rtsp'>('client');
  const [dvrTestResult, setDvrTestResult] = useState<{ success: boolean; response_time_ms: number; snapshot_preview?: string; error?: string } | null>(null);
  const [dvrTesting, setDvrTesting] = useState(false);
  const [rtspTestResult, setRtspTestResult] = useState<{ success: boolean; response_time_ms: number; snapshot_preview?: string; error?: string } | null>(null);
  const [rtspTesting, setRtspTesting] = useState(false);
  const snapshotRef = useRef<SnapshotThumbnailHandle>(null);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isStevenAdmin = user?.email === 'steven@mnsa.co.za';
  const timezone = useTimezone();

  // WebSocket for real-time snapshot updates
  const { subscribe } = useDashboardSocket();

  // Subscribe to snapshot_uploaded events - refresh affected thumbnails immediately
  useEffect(() => {
    const unsubscribe = subscribe('snapshot_uploaded', (data: { camera_id: string }) => {
      setSnapshotRefreshKeys((prev) => ({
        ...prev,
        [data.camera_id]: (prev[data.camera_id] || 0) + 1,
      }));
      // Clear requesting state for this camera
      setRequestingSnapshotId((prev) => prev === data.camera_id ? null : prev);
    });
    return unsubscribe;
  }, [subscribe]);

  // Card-level live feed: periodically request snapshots for the active camera
  useEffect(() => {
    if (!liveFeedCameraId) return;
    snapshotsApi.request(liveFeedCameraId).catch(() => {});
    const interval = setInterval(() => {
      snapshotsApi.request(liveFeedCameraId).catch(() => {});
    }, 3000);
    return () => clearInterval(interval);
  }, [liveFeedCameraId]);

  // Modal-level live feed: periodically request snapshots when active in modal
  useEffect(() => {
    if (!liveFeedActive || !selectedCamera?.is_online) return;
    snapshotsApi.request(selectedCamera.id).catch(() => {});
    const interval = setInterval(() => {
      snapshotsApi.request(selectedCamera.id).catch(() => {});
    }, 3000);
    return () => clearInterval(interval);
  }, [liveFeedActive, selectedCamera?.id, selectedCamera?.is_online]);

  const { data: cameras, isLoading } = useQuery({
    queryKey: ['cameras'],
    queryFn: () => camerasApi.list(),
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });

  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: () => branchesApi.list(),
  });

  // Keep selectedCamera in sync with query data when it refreshes
  useEffect(() => {
    if (selectedCamera && cameras) {
      const updated = cameras.find(c => c.id === selectedCamera.id);
      if (updated) {
        setSelectedCamera(updated);
      }
    }
  }, [cameras]);

  // Fetch live stats for selected camera
  const { data: liveStats } = useQuery({
    queryKey: ['camera-live-stats', selectedCamera?.id],
    queryFn: () => camerasApi.getLiveStats(selectedCamera!.id),
    enabled: !!selectedCamera?.is_online,
    refetchInterval: 3000, // Refresh every 3 seconds when modal is open
  });

  // Fetch settings history for selected camera
  const { data: settingsHistory } = useQuery({
    queryKey: ['camera-settings-history', selectedCamera?.id],
    queryFn: () => camerasApi.getSettingsHistory(selectedCamera!.id),
    enabled: !!selectedCamera,
  });

  // Fetch all zones for overlay display
  const { data: allZones } = useQuery({
    queryKey: ['all-zones'],
    queryFn: () => zonesApi.list(),
    refetchInterval: 30000, // Refresh zone definitions every 30 seconds
  });

  // Fetch zone counts for all online cameras with zone detection enabled
  const [zoneCounts, setZoneCounts] = useState<Map<string, Map<string, number>>>(new Map());

  useEffect(() => {
    if (!cameras || !allZones) return;

    // Get cameras that have zones and are online with zone detection
    const camerasWithZones = cameras.filter(
      (c) => c.is_online && (c.detection_mode === 'zone' || c.detection_mode === 'both')
    );

    if (camerasWithZones.length === 0) return;

    const fetchCounts = async () => {
      const newCounts = new Map<string, Map<string, number>>();

      await Promise.all(
        camerasWithZones.map(async (camera) => {
          try {
            const counts = await zonesApi.getLatestCounts(camera.id);
            const cameraCountsMap = new Map<string, number>();
            counts.forEach((c: LatestZoneCount) => {
              cameraCountsMap.set(c.zone_id, c.count);
            });
            newCounts.set(camera.id, cameraCountsMap);
          } catch {
            // Ignore errors for individual cameras
          }
        })
      );

      setZoneCounts(newCounts);
    };

    // Initial fetch
    fetchCounts();

    // Poll every 5 seconds
    const interval = setInterval(fetchCounts, 5000);

    return () => clearInterval(interval);
  }, [cameras, allZones]);

  // Helper to get zones for a specific camera
  const getZonesForCamera = (cameraId: string): Zone[] => {
    return allZones?.filter((z) => z.camera_id === cameraId) || [];
  };

  // Filter and group cameras by branch
  const filteredCameras = useMemo(() => {
    if (!cameras) return [];
    if (!filterBranchId) return cameras;
    if (filterBranchId === 'unassigned') return cameras.filter((c) => !c.branch_id);
    return cameras.filter((c) => c.branch_id === filterBranchId);
  }, [cameras, filterBranchId]);

  const groupedCameras = useMemo(() => {
    if (!branches || !filteredCameras.length) return { branches: [], unassigned: filteredCameras };

    const branchGroups = branches
      .map((branch) => ({
        branch,
        cameras: filteredCameras.filter((c) => c.branch_id === branch.id),
      }))
      .filter((group) => group.cameras.length > 0);

    const unassigned = filteredCameras.filter((c) => !c.branch_id);

    return { branches: branchGroups, unassigned };
  }, [branches, filteredCameras]);

  const registerMutation = useMutation({
    mutationFn: ({ name, location, branchId }: { name: string; location?: string; branchId?: string }) =>
      camerasApi.register(name, location, branchId),
    onSuccess: (data) => {
      setNewCamera(data);
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('Camera registered');
    },
    onError: () => {
      toast.error('Failed to register camera');
    },
  });

  const registerDvrMutation = useMutation({
    mutationFn: (data: import('../types').RegisterDvrCamera) =>
      camerasApi.registerDvr(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('DVR camera registered');
      closeModal();
    },
    onError: (err: any) => {
      const msg = err?.response?.data || 'Failed to register DVR camera';
      toast.error(typeof msg === 'string' ? msg : 'Failed to register DVR camera');
    },
  });

  const registerRtspMutation = useMutation({
    mutationFn: (data: import('../types').RegisterRtspCamera) =>
      camerasApi.registerRtsp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('RTSP camera registered');
      closeModal();
    },
    onError: (err: any) => {
      const msg = err?.response?.data || 'Failed to register RTSP camera';
      toast.error(typeof msg === 'string' ? msg : 'Failed to register RTSP camera');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (id: string) => camerasApi.regenerateApiKey(id),
    onSuccess: (data) => {
      setRegeneratedKey(data);
      toast.success('API key regenerated');
    },
    onError: () => {
      toast.error('Failed to regenerate API key');
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: ({ id, settings }: { id: string; settings: { face_retention_minutes?: number; face_match_threshold?: number; detection_mode?: DetectionMode; detection_confidence?: number; detection_input_size?: number; stream_quality?: number; tracking_spatial_weight?: number; tracking_snap_threshold?: number } }) =>
      camerasApi.updateSettings(id, settings),
    onSuccess: (updatedCamera) => {
      setSelectedCamera(updatedCamera);
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      queryClient.invalidateQueries({ queryKey: ['camera-settings-history', updatedCamera.id] });
      toast.success('Settings saved');
    },
    onError: () => {
      toast.error('Failed to save settings');
    },
  });

  const updateCameraMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; location?: string; branch_id?: string | null } }) =>
      camerasApi.update(id, data),
    onSuccess: (updatedCamera) => {
      setSelectedCamera(updatedCamera);
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('Camera updated');
    },
    onError: () => {
      toast.error('Failed to update camera');
    },
  });

  const deleteCameraMutation = useMutation({
    mutationFn: (id: string) => camerasApi.delete(id),
    onSuccess: () => {
      closeDetailsModal();
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('Camera deleted');
    },
    onError: () => {
      toast.error('Failed to delete camera');
    },
  });

  const moveCameraMutation = useMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      camerasApi.move(id, organizationId),
    onSuccess: () => {
      closeDetailsModal();
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('Camera moved');
    },
    onError: () => {
      toast.error('Failed to move camera');
    },
  });

  // Fetch organizations for move dialog
  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
    enabled: showMoveModal,
  });

  const handleAddCamera = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    registerMutation.mutate({
      name: formData.get('name') as string,
      location: formData.get('location') as string || undefined,
      branchId: addBranchId || undefined,
    });
  };

  const handleAddDvrCamera = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    registerDvrMutation.mutate({
      name: formData.get('dvr_name') as string || `DVR Ch${formData.get('dvr_channel')}`,
      location: formData.get('dvr_location') as string || undefined,
      branch_id: addBranchId || undefined,
      dvr_host: formData.get('dvr_host') as string,
      dvr_port: parseInt(formData.get('dvr_port') as string) || 80,
      dvr_channel: parseInt(formData.get('dvr_channel') as string) || 1,
      dvr_username: formData.get('dvr_username') as string || 'admin',
      dvr_password: formData.get('dvr_password') as string || '',
      poll_interval_ms: parseInt(formData.get('dvr_poll_interval') as string) || 3000,
    });
  };

  const handleTestDvr = async (form: HTMLFormElement) => {
    const formData = new FormData(form);
    setDvrTesting(true);
    setDvrTestResult(null);
    try {
      const result = await camerasApi.testDvrConnection({
        dvr_host: formData.get('dvr_host') as string,
        dvr_port: parseInt(formData.get('dvr_port') as string) || 80,
        dvr_channel: parseInt(formData.get('dvr_channel') as string) || 1,
        dvr_username: formData.get('dvr_username') as string || 'admin',
        dvr_password: formData.get('dvr_password') as string || '',
      });
      setDvrTestResult(result);
    } catch {
      setDvrTestResult({ success: false, response_time_ms: 0, error: 'Connection test failed' });
    } finally {
      setDvrTesting(false);
    }
  };

  const handleAddRtspCamera = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    registerRtspMutation.mutate({
      name: formData.get('rtsp_name') as string || 'RTSP Camera',
      location: formData.get('rtsp_location') as string || undefined,
      branch_id: addBranchId || undefined,
      rtsp_url: formData.get('rtsp_url') as string,
      rtsp_username: formData.get('rtsp_username') as string || 'admin',
      rtsp_password: formData.get('rtsp_password') as string || '',
      frame_interval_ms: parseInt(formData.get('rtsp_frame_interval') as string) || 5000,
    });
  };

  const handleTestRtsp = async (form: HTMLFormElement) => {
    const formData = new FormData(form);
    setRtspTesting(true);
    setRtspTestResult(null);
    try {
      const result = await camerasApi.testRtspConnection({
        rtsp_url: formData.get('rtsp_url') as string,
        rtsp_username: formData.get('rtsp_username') as string || 'admin',
        rtsp_password: formData.get('rtsp_password') as string || '',
      });
      setRtspTestResult(result);
    } catch {
      setRtspTestResult({ success: false, response_time_ms: 0, error: 'Connection test failed' });
    } finally {
      setRtspTesting(false);
    }
  };

  const copyApiKey = async () => {
    if (newCamera) {
      await navigator.clipboard.writeText(newCamera.api_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const copyCameraId = async (id: string) => {
    await navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setNewCamera(null);
    setAddBranchId('');
    setAddCameraTab('client');
    setDvrTestResult(null);
    setDvrTesting(false);
    setRtspTestResult(null);
    setRtspTesting(false);
  };

  const closeDetailsModal = () => {
    setSelectedCamera(null);
    setRegeneratedKey(null);
    setCopiedId(false);
    setCopiedKey(false);
    setIsEditing(false);
    setShowMoveModal(false);
    setShowDeleteConfirm(false);
    setLiveFeedActive(false);
    setRequestingSnapshot(false);
  };

  const startEditing = () => {
    if (selectedCamera) {
      setEditName(selectedCamera.name);
      setEditLocation(selectedCamera.location || '');
      setEditBranchId(selectedCamera.branch_id || '');
      setIsEditing(true);
    }
  };

  const handleSaveEdit = () => {
    if (selectedCamera) {
      updateCameraMutation.mutate({
        id: selectedCamera.id,
        data: { name: editName, location: editLocation || undefined, branch_id: editBranchId || null },
      });
    }
  };

  const copyRegeneratedKey = async () => {
    if (regeneratedKey) {
      await navigator.clipboard.writeText(regeneratedKey.api_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Cameras</h1>
          <p className="text-gray-500 dark:text-slate-400">Manage your connected camera devices</p>
        </div>
        <div className="flex items-center space-x-3">
          {branches && branches.length > 0 && (
            <select
              value={filterBranchId}
              onChange={(e) => setFilterBranchId(e.target.value)}
              className="text-sm border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 dark:text-white"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
              <option value="unassigned">Unassigned</option>
            </select>
          )}
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Camera
          </Button>
        </div>
      </div>

      {/* Camera Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading cameras...</div>
      ) : filteredCameras.length > 0 ? (
        <div className="space-y-6">
          {/* Branch groups */}
          {groupedCameras.branches.map(({ branch, cameras: branchCameras }) => (
            <div key={branch.id}>
              <div className="flex items-center space-x-2 mb-3">
                <Building2 className="w-4 h-4 text-primary-500 dark:text-primary-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">{branch.name}</h3>
                <span className="text-sm text-gray-500 dark:text-slate-400">({branchCameras.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {branchCameras.map((camera) => (
                  <CameraCard
                    key={camera.id}
                    camera={camera}
                    getZonesForCamera={getZonesForCamera}
                    zoneCounts={zoneCounts}
                    onClick={() => setSelectedCamera(camera)}
                    refreshKey={snapshotRefreshKeys[camera.id] || 0}
                    isLiveFeed={liveFeedCameraId === camera.id}
                    isRequesting={requestingSnapshotId === camera.id}
                    onToggleLiveFeed={() => setLiveFeedCameraId((prev) => prev === camera.id ? null : camera.id)}
                    onRequestSnapshot={() => {
                      setRequestingSnapshotId(camera.id);
                      snapshotsApi.request(camera.id).catch(() => setRequestingSnapshotId(null));
                    }}
                    timezone={timezone}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Unassigned cameras */}
          {groupedCameras.unassigned.length > 0 && (
            <div>
              {groupedCameras.branches.length > 0 && (
                <div className="flex items-center space-x-2 mb-3">
                  <h3 className="font-semibold text-gray-500 dark:text-slate-400">Unassigned</h3>
                  <span className="text-sm text-gray-400 dark:text-slate-500">({groupedCameras.unassigned.length})</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedCameras.unassigned.map((camera) => (
                  <CameraCard
                    key={camera.id}
                    camera={camera}
                    getZonesForCamera={getZonesForCamera}
                    zoneCounts={zoneCounts}
                    onClick={() => setSelectedCamera(camera)}
                    refreshKey={snapshotRefreshKeys[camera.id] || 0}
                    isLiveFeed={liveFeedCameraId === camera.id}
                    isRequesting={requestingSnapshotId === camera.id}
                    onToggleLiveFeed={() => setLiveFeedCameraId((prev) => prev === camera.id ? null : camera.id)}
                    onRequestSnapshot={() => {
                      setRequestingSnapshotId(camera.id);
                      snapshotsApi.request(camera.id).catch(() => setRequestingSnapshotId(null));
                    }}
                    timezone={timezone}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Card className="text-center py-12">
          <Camera className="w-12 h-12 mx-auto text-gray-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No cameras yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">
            Add your first camera to start collecting analytics
          </p>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Camera
          </Button>
        </Card>
      )}

      {/* Add Camera Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:border dark:border-slate-700">
            {!newCamera ? (
              <>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Add New Camera
                </h2>
                {/* Tab switcher */}
                <div className="flex rounded-lg bg-gray-100 dark:bg-slate-800 p-1 mb-4">
                  <button
                    type="button"
                    onClick={() => { setAddCameraTab('client'); setDvrTestResult(null); setRtspTestResult(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      addCameraTab === 'client'
                        ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                    }`}
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Client Camera
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddCameraTab('dvr'); setDvrTestResult(null); setRtspTestResult(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      addCameraTab === 'dvr'
                        ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" />
                    Network DVR
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddCameraTab('rtsp'); setDvrTestResult(null); setRtspTestResult(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      addCameraTab === 'rtsp'
                        ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                    }`}
                  >
                    <Video className="w-3.5 h-3.5" />
                    IP Camera
                  </button>
                </div>

                {addCameraTab === 'client' && (
                  <form onSubmit={handleAddCamera} className="space-y-4">
                    <Input
                      label="Camera Name"
                      name="name"
                      placeholder="e.g., Entrance Camera"
                      required
                    />
                    <Input
                      label="Location (optional)"
                      name="location"
                      placeholder="e.g., Main Store Entrance"
                    />
                    {branches && branches.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Branch (optional)</label>
                        <select
                          value={addBranchId}
                          onChange={(e) => setAddBranchId(e.target.value)}
                          className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                        >
                          <option value="">No branch</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button type="button" variant="outline" onClick={closeModal}>
                        Cancel
                      </Button>
                      <Button type="submit" isLoading={registerMutation.isPending}>
                        Register Camera
                      </Button>
                    </div>
                  </form>
                )}
                {addCameraTab === 'dvr' && (
                  <form id="dvr-form" onSubmit={handleAddDvrCamera} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Input
                          label="DVR IP Address"
                          name="dvr_host"
                          placeholder="e.g., 192.168.1.100"
                          required
                        />
                      </div>
                      <Input
                        label="HTTP Port"
                        name="dvr_port"
                        type="number"
                        defaultValue="80"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Channel</label>
                        <select
                          name="dvr_channel"
                          defaultValue="1"
                          className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                        >
                          <option value="1">Channel 1</option>
                          <option value="2">Channel 2</option>
                          <option value="3">Channel 3</option>
                          <option value="4">Channel 4</option>
                        </select>
                      </div>
                      <Input
                        label="Username"
                        name="dvr_username"
                        defaultValue="admin"
                      />
                      <Input
                        label="Password"
                        name="dvr_password"
                        type="password"
                        placeholder="(blank)"
                      />
                    </div>
                    <Input
                      label="Camera Name"
                      name="dvr_name"
                      placeholder="e.g., Office DVR Ch1"
                    />
                    <Input
                      label="Location (optional)"
                      name="dvr_location"
                      placeholder="e.g., Office Reception"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Snapshot Interval</label>
                      <select
                        name="dvr_poll_interval"
                        defaultValue="3000"
                        className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                      >
                        <option value="1000">1 second (live)</option>
                        <option value="2000">2 seconds</option>
                        <option value="3000">3 seconds (default)</option>
                        <option value="5000">5 seconds</option>
                        <option value="10000">10 seconds</option>
                        <option value="30000">30 seconds</option>
                      </select>
                    </div>
                    {branches && branches.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Branch (optional)</label>
                        <select
                          value={addBranchId}
                          onChange={(e) => setAddBranchId(e.target.value)}
                          className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                        >
                          <option value="">No branch</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Test connection result */}
                    {dvrTestResult && (
                      <div className={`rounded-lg p-3 ${dvrTestResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {dvrTestResult.success ? (
                            <>
                              <Wifi className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-700 dark:text-green-300">Connected ({dvrTestResult.response_time_ms}ms)</span>
                            </>
                          ) : (
                            <>
                              <WifiOff className="w-4 h-4 text-red-600" />
                              <span className="text-sm font-medium text-red-700 dark:text-red-300">{dvrTestResult.error || 'Connection failed'}</span>
                            </>
                          )}
                        </div>
                        {dvrTestResult.snapshot_preview && (
                          <img
                            src={dvrTestResult.snapshot_preview}
                            alt="DVR Preview"
                            className="w-full rounded-md"
                          />
                        )}
                      </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                      <Button type="button" variant="outline" onClick={closeModal}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        isLoading={dvrTesting}
                        onClick={() => {
                          const form = document.getElementById('dvr-form') as HTMLFormElement;
                          if (form?.reportValidity()) handleTestDvr(form);
                        }}
                      >
                        <Wifi className="w-4 h-4 mr-1.5" />
                        Test
                      </Button>
                      <Button type="submit" isLoading={registerDvrMutation.isPending}>
                        Register DVR
                      </Button>
                    </div>
                  </form>
                )}
                {addCameraTab === 'rtsp' && (
                  <form id="rtsp-form" onSubmit={handleAddRtspCamera} className="space-y-3">
                    <div className="col-span-2">
                      <Input
                        label="RTSP URL"
                        name="rtsp_url"
                        placeholder="rtsp://192.168.1.50:554/stream1"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Username"
                        name="rtsp_username"
                        defaultValue="admin"
                      />
                      <Input
                        label="Password"
                        name="rtsp_password"
                        type="password"
                        placeholder="(blank)"
                      />
                    </div>
                    <Input
                      label="Camera Name"
                      name="rtsp_name"
                      placeholder="e.g., Parking Lot Camera"
                    />
                    <Input
                      label="Location (optional)"
                      name="rtsp_location"
                      placeholder="e.g., Front Parking"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Frame Capture Interval</label>
                      <select
                        name="rtsp_frame_interval"
                        defaultValue="5000"
                        className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                      >
                        <option value="3000">3 seconds</option>
                        <option value="5000">5 seconds (default)</option>
                        <option value="10000">10 seconds</option>
                        <option value="30000">30 seconds</option>
                        <option value="60000">1 minute</option>
                      </select>
                    </div>
                    {branches && branches.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Branch (optional)</label>
                        <select
                          value={addBranchId}
                          onChange={(e) => setAddBranchId(e.target.value)}
                          className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                        >
                          <option value="">No branch</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Test connection result */}
                    {rtspTestResult && (
                      <div className={`rounded-lg p-3 ${rtspTestResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {rtspTestResult.success ? (
                            <>
                              <Wifi className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-700 dark:text-green-300">Connected ({rtspTestResult.response_time_ms}ms)</span>
                            </>
                          ) : (
                            <>
                              <WifiOff className="w-4 h-4 text-red-600" />
                              <span className="text-sm font-medium text-red-700 dark:text-red-300">{rtspTestResult.error || 'Connection failed'}</span>
                            </>
                          )}
                        </div>
                        {rtspTestResult.snapshot_preview && (
                          <img
                            src={rtspTestResult.snapshot_preview}
                            alt="RTSP Preview"
                            className="w-full rounded-md"
                          />
                        )}
                      </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                      <Button type="button" variant="outline" onClick={closeModal}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        isLoading={rtspTesting}
                        onClick={() => {
                          const form = document.getElementById('rtsp-form') as HTMLFormElement;
                          if (form?.reportValidity()) handleTestRtsp(form);
                        }}
                      >
                        <Wifi className="w-4 h-4 mr-1.5" />
                        Test
                      </Button>
                      <Button type="submit" isLoading={registerRtspMutation.isPending}>
                        Register Camera
                      </Button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Camera Registered!
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                  Save these credentials - the API key won't be shown again.
                </p>
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 mb-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Camera ID
                    </label>
                    <code className="text-sm font-mono break-all text-gray-900 dark:text-white">
                      {newCamera.id}
                    </code>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      API Key
                    </label>
                    <div className="flex items-center">
                      <code className="flex-1 text-sm font-mono break-all text-gray-900 dark:text-white">
                        {newCamera.api_key}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyApiKey}
                        className="ml-2"
                      >
                        {copiedKey ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Use both the Camera ID and API Key in your HumanVu client app to connect this camera.
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button onClick={closeModal}>Done</Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Camera Details Modal */}
      {selectedCamera && !showMoveModal && !showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto dark:border dark:border-slate-700">
            {/* Live camera feed preview */}
            <CameraSnapshotThumbnail
              ref={snapshotRef}
              cameraId={selectedCamera.id}
              cameraName={selectedCamera.name}
              isOnline={selectedCamera.is_online}
              zones={isStevenAdmin ? getZonesForCamera(selectedCamera.id) : []}
              zoneCounts={zoneCounts.get(selectedCamera.id) || new Map()}
              refreshInterval={selectedCamera.camera_type === 'dvr' || selectedCamera.camera_type === 'rtsp' ? 3000 : (liveFeedActive ? 3000 : undefined)}
              refreshKey={snapshotRefreshKeys[selectedCamera.id] || 0}
            />

            {/* Snapshot controls */}
            <div className="flex items-center gap-2 mb-3">
              {selectedCamera.camera_type === 'rtsp' ? (
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-sm">
                    <Video className="w-4 h-4" />
                    RTSP Stream — Auto-updating
                  </div>
                </div>
              ) : selectedCamera.camera_type === 'dvr' ? (
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-sm">
                    <Monitor className="w-4 h-4" />
                    DVR Channel {selectedCamera.dvr_channel} — Auto-updating
                  </div>
                </div>
              ) : (
                <>
                  <Button
                    variant={liveFeedActive ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setLiveFeedActive(!liveFeedActive)}
                    disabled={!selectedCamera.is_online}
                    className="flex-1"
                  >
                    <Video className="w-4 h-4 mr-1.5" />
                    {liveFeedActive ? 'Live Feed On' : 'Live Feed'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRequestingSnapshot(true);
                      snapshotsApi.request(selectedCamera.id)
                        .then(() => {
                          toast.success('Snapshot requested — refreshing in a few seconds');
                          setTimeout(() => {
                            snapshotRef.current?.refresh();
                            setRequestingSnapshot(false);
                          }, 3000);
                        })
                        .catch(() => {
                          toast.error('Failed to request snapshot');
                          setRequestingSnapshot(false);
                        });
                    }}
                    disabled={!selectedCamera.is_online || requestingSnapshot}
                    className="flex-1"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${requestingSnapshot ? 'animate-spin' : ''}`} />
                    {requestingSnapshot ? 'Capturing...' : 'New Snapshot'}
                  </Button>
                </>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedCamera.name}
                </h2>
                <span
                  className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                    !selectedCamera.is_active
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : selectedCamera.is_online
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                  }`}
                >
                  {!selectedCamera.is_active ? 'Removed' : selectedCamera.is_online ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                {!isEditing && (
                  <>
                    <button
                      onClick={startEditing}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                      title="Edit camera"
                    >
                      <Pencil className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    </button>
                    <button
                      onClick={() => setShowMoveModal(true)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                      title="Move to another organization"
                    >
                      <ArrowRightLeft className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Delete camera"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </>
                )}
                <button
                  onClick={closeDetailsModal}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Name
                    </label>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Camera name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Location
                    </label>
                    <Input
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder="Location (optional)"
                    />
                  </div>
                  {branches && branches.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                        Branch
                      </label>
                      <select
                        value={editBranchId}
                        onChange={(e) => setEditBranchId(e.target.value)}
                        className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                      >
                        <option value="">No branch</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEdit}
                      isLoading={updateCameraMutation.isPending}
                      className="flex-1"
                    >
                      Save
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Name
                    </label>
                    <p className="text-gray-900 dark:text-white font-medium">{selectedCamera.name}</p>
                  </div>

                  {selectedCamera.location && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                        Location
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedCamera.location}</p>
                    </div>
                  )}

                  {selectedCamera.branch_id && branches && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                        Branch
                      </label>
                      <div className="flex items-center text-gray-900 dark:text-white">
                        <Building2 className="w-4 h-4 text-primary-500 dark:text-primary-400 mr-1.5" />
                        {branches.find((b) => b.id === selectedCamera.branch_id)?.name || 'Unknown'}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Camera ID
                </label>
                <div className="flex items-center bg-gray-50 dark:bg-slate-900 rounded-lg p-2">
                  <code className="flex-1 text-sm font-mono break-all text-gray-900 dark:text-white">
                    {selectedCamera.id}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyCameraId(selectedCamera.id)}
                    className="ml-2"
                  >
                    {copiedId ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {selectedCamera.client_node_id && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                    Connected via Client
                  </label>
                  <p className="text-gray-900 dark:text-white font-mono text-sm">{selectedCamera.client_node_id}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Status
                </label>
                <span
                  className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                    !selectedCamera.is_active
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : selectedCamera.is_online
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                  }`}
                >
                  {!selectedCamera.is_active ? 'Removed' : selectedCamera.is_online ? 'Online' : 'Offline'}
                </span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Last Seen
                </label>
                <p className="text-gray-900 dark:text-white">
                  {formatInTimezone(selectedCamera.last_seen_at, timezone)}
                </p>
              </div>

              {/* Detection Mode Selector */}
              <div className="border-t dark:border-slate-700 pt-4 mt-4">
                <div className="flex items-center mb-2">
                  <Layers className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    Detection Mode
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                  Choose what this camera detects and how.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { mode: 'face' as DetectionMode, icon: UserCircle, label: 'Face', desc: 'Close-range faces' },
                    { mode: 'people' as DetectionMode, icon: Users, label: 'People', desc: 'Full-body counting' },
                    { mode: 'hybrid' as DetectionMode, icon: Eye, label: 'Hybrid', desc: 'Bodies + faces' },
                    { mode: 'zone' as DetectionMode, icon: Grid3X3, label: 'Zone', desc: 'Item monitoring' },
                    { mode: 'both' as DetectionMode, icon: Layers, label: 'Both', desc: 'Face + zones' },
                    { mode: 'disabled' as DetectionMode, icon: PauseCircle, label: 'Disabled', desc: 'Detection off' },
                  ].filter(({ mode }) => isStevenAdmin || !['zone', 'both'].includes(mode))
                  ).map(({ mode, icon: Icon, label, desc }) => (
                    <button
                      key={mode}
                      onClick={() => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { detection_mode: mode },
                        });
                      }}
                      className={`flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                        selectedCamera.detection_mode === mode
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 text-gray-600 dark:text-slate-300'
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-1" />
                      <span className="text-sm font-medium">{label}</span>
                      <span className="text-xs opacity-75">{desc}</span>
                    </button>
                  ))}
                </div>

                {/* Configure Zones Button - show for zone or both modes, only for steven */}
                {isStevenAdmin && (selectedCamera.detection_mode === 'zone' || selectedCamera.detection_mode === 'both') && (
                  <Button
                    variant="outline"
                    className="w-full mt-3"
                    onClick={() => setShowZoneEditor(true)}
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    Configure Zones
                  </Button>
                )}
              </div>

              {/* Face Detection Settings - only show for face or both modes */}
              {(selectedCamera.detection_mode === 'face' || selectedCamera.detection_mode === 'both' || selectedCamera.detection_mode === 'hybrid') && (
                <>
                  {/* Face Retention Setting */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <div className="flex items-center mb-2">
                      <Clock className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        Face Retention
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      How long to remember faces before counting them as new visitors again.
                      Longer times prevent staff from being counted multiple times.
                    </p>
                    <Select
                      options={[
                        { value: '5', label: '5 minutes' },
                        { value: '15', label: '15 minutes' },
                        { value: '30', label: '30 minutes' },
                        { value: '60', label: '1 hour' },
                        { value: '120', label: '2 hours' },
                        { value: '240', label: '4 hours' },
                        { value: '480', label: '8 hours' },
                        { value: '720', label: '12 hours' },
                        { value: '1440', label: '24 hours' },
                      ]}
                      value={String(selectedCamera.face_retention_minutes)}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { face_retention_minutes: parseInt(e.target.value, 10) },
                        });
                      }}
                      className="w-full"
                    />
                  </div>

                  {/* Face Match Threshold Setting */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <div className="flex items-center mb-2">
                      <Sliders className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        Face Match Sensitivity
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      Lower = stricter (fewer false matches, may count same person twice).
                      Higher = lenient (catches same person better, may merge different people).
                    </p>
                    <input
                      type="range"
                      min="0.3"
                      max="0.8"
                      step="0.05"
                      value={selectedCamera.face_match_threshold}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { face_match_threshold: parseFloat(e.target.value) },
                        });
                      }}
                      className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mt-1">
                      <span>Strict (0.3)</span>
                      <span className="font-medium text-gray-700 dark:text-slate-200">{selectedCamera.face_match_threshold.toFixed(2)}</span>
                      <span>Lenient (0.8)</span>
                    </div>
                  </div>

                  {/* Tracking Spatial Weight Setting */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <div className="flex items-center mb-2">
                      <Sliders className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        Tracking Spatial Weight
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      How much position vs appearance matters when tracking.
                      Higher = relies more on where the face is, lower = relies more on what it looks like.
                    </p>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={selectedCamera.tracking_spatial_weight}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { tracking_spatial_weight: parseFloat(e.target.value) },
                        });
                      }}
                      className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mt-1">
                      <span>Appearance only (0.0)</span>
                      <span className="font-medium text-gray-700 dark:text-slate-200">{selectedCamera.tracking_spatial_weight.toFixed(2)}</span>
                      <span>Position only (1.0)</span>
                    </div>
                  </div>

                  {/* Tracking Snap Threshold Setting */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <div className="flex items-center mb-2">
                      <Sliders className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        Tracking Snap Threshold
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      How quickly boxes snap to corrected positions.
                      Lower = faster correction, higher = smoother movement.
                    </p>
                    <input
                      type="range"
                      min="0.5"
                      max="3.0"
                      step="0.1"
                      value={selectedCamera.tracking_snap_threshold}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { tracking_snap_threshold: parseFloat(e.target.value) },
                        });
                      }}
                      className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mt-1">
                      <span>Snappy (0.5)</span>
                      <span className="font-medium text-gray-700 dark:text-slate-200">{selectedCamera.tracking_snap_threshold.toFixed(1)}</span>
                      <span>Smooth (3.0)</span>
                    </div>
                  </div>

                  {/* Detection Confidence Setting */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <div className="flex items-center mb-2">
                      <Eye className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        Detection Confidence
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      Minimum confidence score to accept a face detection.
                      Lower = detects faces further away but may have false positives.
                      Higher = only detects clear, close-up faces.
                    </p>
                    <input
                      type="range"
                      min="0.1"
                      max="0.9"
                      step="0.05"
                      value={selectedCamera.detection_confidence}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { detection_confidence: parseFloat(e.target.value) },
                        });
                      }}
                      className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mt-1">
                      <span>Far (0.1)</span>
                      <span className="font-medium text-gray-700 dark:text-slate-200">{selectedCamera.detection_confidence.toFixed(2)}</span>
                      <span>Close (0.9)</span>
                    </div>
                  </div>

                  {/* Detection Input Size Setting */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <div className="flex items-center mb-2">
                      <Maximize2 className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        Detection Resolution
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      Higher resolution detects smaller/distant faces but uses more CPU.
                      Lower resolution is faster but only picks up close faces.
                    </p>
                    <Select
                      options={[
                        { value: '128', label: '128px (fastest, close faces only)' },
                        { value: '160', label: '160px' },
                        { value: '224', label: '224px' },
                        { value: '320', label: '320px (balanced)' },
                        { value: '416', label: '416px (default before tuning)' },
                        { value: '512', label: '512px' },
                        { value: '608', label: '608px (best range, slower)' },
                      ]}
                      value={String(selectedCamera.detection_input_size)}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({
                          id: selectedCamera.id,
                          settings: { detection_input_size: parseInt(e.target.value, 10) },
                        });
                      }}
                      className="w-full"
                    />
                  </div>

                  {/* Stream Quality - RTSP cameras only */}
                  {selectedCamera.camera_type === 'rtsp' && (
                    <div className="border-t dark:border-slate-700 pt-4 mt-4">
                      <div className="flex items-center mb-2">
                        <Video className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                        <label className="text-sm font-medium text-gray-900 dark:text-white">
                          Stream Quality
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                        JPEG compression quality for live stream frames. Higher quality uses more bandwidth.
                        Takes effect on next capture restart.
                      </p>
                      <Select
                        options={[
                          { value: '1', label: 'Maximum (1) — Largest frames' },
                          { value: '2', label: 'High (2) — Default' },
                          { value: '3', label: 'Good (3)' },
                          { value: '5', label: 'Balanced (5)' },
                          { value: '8', label: 'Low bandwidth (8)' },
                          { value: '10', label: 'Minimum (10) — Smallest frames' },
                        ]}
                        value={String(selectedCamera.stream_quality)}
                        onChange={(e) => {
                          updateSettingsMutation.mutate({
                            id: selectedCamera.id,
                            settings: { stream_quality: parseInt(e.target.value, 10) },
                          });
                        }}
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Recommended Presets */}
                  <div className="border-t dark:border-slate-700 pt-4 mt-4">
                    <p className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">Recommended presets</p>
                    <div className="space-y-1.5">
                      {[
                        { label: 'Entrance / Exit (3-5m)', confidence: 0.35, size: 608, spatialWeight: 0.5, snapThreshold: 1.0, desc: 'Catches people walking through' },
                        { label: 'Checkout counter (1-2m)', confidence: 0.45, size: 416, spatialWeight: 0.3, snapThreshold: 2.0, desc: 'Close range, fewer false positives' },
                        { label: 'Wide-angle overview (5-10m)', confidence: 0.25, size: 608, spatialWeight: 0.6, snapThreshold: 1.0, desc: 'Maximum sensitivity for distant faces' },
                      ].map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            updateSettingsMutation.mutate({
                              id: selectedCamera.id,
                              settings: { detection_confidence: preset.confidence, detection_input_size: preset.size, tracking_spatial_weight: preset.spatialWeight, tracking_snap_threshold: preset.snapThreshold },
                            });
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-300 dark:hover:border-primary-700 transition-colors cursor-pointer"
                        >
                          <span className="text-xs font-medium text-gray-900 dark:text-white">{preset.label}</span>
                          <span className="block text-xs text-gray-500 dark:text-slate-400">
                            Confidence {preset.confidence} / Resolution {preset.size}px — {preset.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Settings History */}
              {settingsHistory && settingsHistory.length > 0 && (
                <details className="border-t dark:border-slate-700 pt-4 mt-4">
                  <summary className="flex items-center cursor-pointer text-sm font-medium text-gray-900 dark:text-white">
                    <History className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                    Settings History
                  </summary>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {settingsHistory.map((entry) => (
                      <div key={entry.id} className="text-xs text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-900 p-2 rounded">
                        <span className="font-medium">{entry.setting_name.replace(/_/g, ' ')}</span>:{' '}
                        {entry.old_value ?? 'unset'} → {entry.new_value}
                        <span className="text-gray-400 dark:text-slate-500 ml-2">
                          {formatInTimezone(entry.changed_at, timezone)}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Live Stats Section */}
              {selectedCamera.is_online && (
                <div className="border-t dark:border-slate-700 pt-4 mt-4">
                  <div className="flex items-center mb-3">
                    <Activity className="w-4 h-4 text-green-600 mr-2" />
                    <label className="text-sm font-medium text-gray-900 dark:text-white">
                      Live Stats (last 60 seconds)
                    </label>
                  </div>

                  {liveStats ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                        <div className="flex items-center">
                          <Users className="w-5 h-5 text-blue-600 mr-2" />
                          <span className="text-sm text-gray-700 dark:text-slate-200">Detections</span>
                        </div>
                        <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                          {liveStats.detection_count}
                        </span>
                      </div>

                      {liveStats.detection_count > 0 && (
                        <>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                              <span className="text-gray-500 dark:text-slate-400">Male</span>
                              <span className="float-right font-medium dark:text-white">{liveStats.male_count}</span>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                              <span className="text-gray-500 dark:text-slate-400">Female</span>
                              <span className="float-right font-medium dark:text-white">{liveStats.female_count}</span>
                            </div>
                          </div>

                          {Object.keys(liveStats.expression_distribution).length > 0 && (
                            <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                              <span className="text-xs text-gray-500 dark:text-slate-400 block mb-1">Top Expressions</span>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(liveStats.expression_distribution)
                                  .sort(([, a], [, b]) => b - a)
                                  .slice(0, 3)
                                  .map(([expr, count]) => (
                                    <span key={expr} className="px-2 py-0.5 bg-white dark:bg-slate-800 rounded text-xs dark:text-white">
                                      {expr.charAt(0).toUpperCase() + expr.slice(1)}: {count}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          )}

                          {liveStats.last_detection && (
                            <div className="text-xs text-gray-500 dark:text-slate-400 text-right">
                              Last detection: {formatTimeInTimezone(liveStats.last_detection, timezone)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-slate-400 text-center py-2">
                      Loading live stats...
                    </div>
                  )}
                </div>
              )}

              {regeneratedKey ? (
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-sm text-green-800 dark:text-green-300 font-medium mb-2">
                    New API Key Generated!
                  </p>
                  <div className="flex items-center bg-white dark:bg-slate-800 rounded p-2">
                    <code className="flex-1 text-sm font-mono break-all text-gray-900 dark:text-white">
                      {regeneratedKey.api_key}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyRegeneratedKey}
                      className="ml-2"
                    >
                      {copiedKey ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                    Save this key now - it won't be shown again!
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
                    <strong>Lost your API Key?</strong> You can regenerate it below.
                    The old key will stop working immediately.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => regenerateMutation.mutate(selectedCamera.id)}
                    isLoading={regenerateMutation.isPending}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate API Key
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <Button onClick={closeDetailsModal}>Close</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Move Camera Modal */}
      {selectedCamera && showMoveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:border dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Move Camera
              </h2>
              <button
                onClick={() => setShowMoveModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-300 mb-4">
              Move <strong>{selectedCamera.name}</strong> to a different organization.
              The camera will no longer appear in your current organization.
            </p>

            {organizations && organizations.length > 0 ? (
              <div className="space-y-4">
                <Select
                  label="Target Organization"
                  options={organizations
                    .filter(org => org.id !== selectedCamera.organization_id)
                    .map(org => ({ value: org.id, label: org.name }))}
                  onChange={(e) => {
                    if (e.target.value) {
                      moveCameraMutation.mutate({
                        id: selectedCamera.id,
                        organizationId: e.target.value,
                      });
                    }
                  }}
                  className="w-full"
                />
                {moveCameraMutation.isPending && (
                  <p className="text-sm text-gray-500 dark:text-slate-400">Moving camera...</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-slate-400">
                No other organizations available.
              </p>
            )}

            <div className="flex justify-end mt-6">
              <Button variant="outline" onClick={() => setShowMoveModal(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {selectedCamera && showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:border dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
                Delete Camera
              </h2>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This will permanently delete the camera{' '}
                <strong>{selectedCamera.name}</strong> and all its associated data.
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteCameraMutation.mutate(selectedCamera.id)}
                isLoading={deleteCameraMutation.isPending}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Camera
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Zone Editor Modal */}
      {selectedCamera && (
        <ZoneEditor
          cameraId={selectedCamera.id}
          cameraName={selectedCamera.name}
          isOpen={showZoneEditor}
          onClose={() => setShowZoneEditor(false)}
        />
      )}
    </div>
  );
}
